"use client";

import React, { useState, useEffect } from 'react';
import { useSubscribedCompanies } from '@/context/SubscribedCompaniesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { createPortal } from 'react-dom';

interface SubscribeButtonProps {
  company: {
    id?: number | string;
    name: string;
    logo?: string;
    location?: string;
    logoColor?: string;
  };
  className?: string;
  variant?: 'follow' | 'subscribe' | 'bell';
}

export default function SubscribeButton({ company, className = '', variant = 'subscribe' }: SubscribeButtonProps) {
  const { data: session, status } = useSession();
  const { subscribe, unsubscribe, isSubscribed } = useSubscribedCompanies();
  const { t, language } = useLanguage();
  const router = useRouter();
  const [showPopup, setShowPopup] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [mounted, setMounted] = useState(false);
  const subscribed = mounted ? isSubscribed(company.name) : false;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const handleToggleSubscribe = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const isLogged = (typeof window !== 'undefined' && localStorage.getItem('isLoggedIn') === 'true') || status === 'authenticated' || !!session;
    if (!isLogged) {
      setShowLoginPopup(true);
      return;
    }

    if (subscribed) {
      unsubscribe(company.name, company.id);
    } else {
      subscribe(company);
      setShowPopup(true);
    }
  };

  if (variant === 'bell') {
    return (
      <>
        <button
          className={className}
          onClick={handleToggleSubscribe}
          title={subscribed ? t('Unfollow company') : t('Follow company')}
          style={{
            background: subscribed ? '#eff6ff' : '#f8fafc',
            border: subscribed ? '1px solid #93c5fd' : '1px solid #e2e8f0',
            color: subscribed ? '#1c7bff' : '#64748b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.2s ease',
            padding: 0
          }}
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill={subscribed ? '#1c7bff' : 'none'} 
            stroke={subscribed ? '#1c7bff' : 'currentColor'} 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </button>

        {mounted && showPopup && typeof window !== 'undefined' && createPortal(
          <div 
            onClick={() => setShowPopup(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.15)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                padding: '24px 32px',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                cursor: 'default'
              }}
            >
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Successfully Followed!')}</h3>
            <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
              {t('You will now receive notifications when')} {company.name} {t('posts new jobs.')}
            </p>
          </div>
          </div>,
          document.body
        )}

        {mounted && showLoginPopup && typeof window !== 'undefined' && createPortal(
          <div 
            onClick={() => setShowLoginPopup(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white', padding: '24px 32px', borderRadius: '16px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxWidth: '90%', width: '350px',
                cursor: 'default'
              }}
            >
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Authentication required')}</h3>
              <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
                {t('Please log in first to follow companies and receive notifications.')}
              </p>
              <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
                <button onClick={() => setShowLoginPopup(false)} style={{ flex: 1, padding: '10px', backgroundColor: '#f3f4f6', color: '#4b5563', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Cancel')}</button>
                <button onClick={() => { setShowLoginPopup(false); router.push(`/${language}/login`); }} style={{ flex: 1, padding: '10px', backgroundColor: '#0b5cd5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Log in')}</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  if (variant === 'follow') {
    return (
      <>
        <button
          className={className}
          onClick={handleToggleSubscribe}
          style={subscribed ? { color: '#16a34a' } : undefined}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path></svg>
          {subscribed ? t('Following') : t('Follow')}
        </button>

        {mounted && showPopup && typeof window !== 'undefined' && createPortal(
          <div 
            onClick={() => setShowPopup(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.15)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                padding: '24px 32px',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                cursor: 'default'
              }}
            >
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Successfully Followed!')}</h3>
            <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
              {t('You will now receive notifications when')} {company.name} {t('posts new jobs.')}
            </p>
          </div>
          </div>,
          document.body
        )}

        {mounted && showLoginPopup && typeof window !== 'undefined' && createPortal(
          <div 
            onClick={() => setShowLoginPopup(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white', padding: '24px 32px', borderRadius: '16px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxWidth: '90%', width: '350px',
                cursor: 'default'
              }}
            >
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Authentication required')}</h3>
              <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
                {t('Please log in first to follow companies and receive notifications.')}
              </p>
              <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
                <button onClick={() => setShowLoginPopup(false)} style={{ flex: 1, padding: '10px', backgroundColor: '#f3f4f6', color: '#4b5563', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Cancel')}</button>
                <button onClick={() => { setShowLoginPopup(false); router.push(`/${language}/login`); }} style={{ flex: 1, padding: '10px', backgroundColor: '#0b5cd5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Log in')}</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <button
        className={className}
        onClick={handleToggleSubscribe}
        style={subscribed ? { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' } : undefined}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path></svg>
        {subscribed ? t('Unsubscribe') : t('Subscribe')}
      </button>

      {mounted && showPopup && typeof window !== 'undefined' && createPortal(
          <div 
            onClick={() => setShowPopup(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.15)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                padding: '24px 32px',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                cursor: 'default'
              }}
            >
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Successfully Followed!')}</h3>
          <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
            {t('You will now receive notifications when')} {company.name} {t('posts new jobs.')}
          </p>
        </div>
        </div>,
        document.body
      )}

      {mounted && showLoginPopup && typeof window !== 'undefined' && createPortal(
        <div 
          onClick={() => setShowLoginPopup(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '24px 32px', borderRadius: '16px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxWidth: '90%', width: '350px',
              cursor: 'default'
            }}
          >
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{t('Authentication required')}</h3>
            <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
              {t('Please log in first to follow companies and receive notifications.')}
            </p>
            <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
              <button onClick={() => setShowLoginPopup(false)} style={{ flex: 1, padding: '10px', backgroundColor: '#f3f4f6', color: '#4b5563', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Cancel')}</button>
              <button onClick={() => { setShowLoginPopup(false); router.push(`/${language}/login`); }} style={{ flex: 1, padding: '10px', backgroundColor: '#0b5cd5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>{t('Log in')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
